import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggerService } from '../logger/logger.service';
import {
    CheckPendingRequestsDto,
    CheckPendingRequestsResponseDto,
} from '../../../common/dtos/check-pending-requests.dto';

interface PendingRequestExistingUserPayload {
    sagId: string;
    granteePoid: string;
}

export interface PendingRequestNewUserPayload {
    sagId: string;
    granteeEmail: string;
}

export type PendingRequestPayload =
    | PendingRequestExistingUserPayload
    | PendingRequestNewUserPayload;

export interface PendingRequestResponse {
    hasPendingRequest: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class PendingRequestService {
    private httpClient = inject(HttpClient);
    private loggerService = inject(LoggerService);


    /**
     * Checks if a pending permission request exists for an existing Vanguard user.
     * 
     * @param sagId - Service Agreement Group ID
     * @param granteePoid - The POID of the grantee (existing Vanguard client)
     * @returns Observable<PendingRequestResponse> - Indicates if a pending request exists
     * 
     * @remarks
     * Used in the Grant Access Flow to prevent duplicate permission requests
     * for users who already have an account.
     */
    checkRequestForExistingUser(
        sagId: string,
        granteePoid: string,
    ): Observable<PendingRequestResponse> {
        return this.checkRequest({
            sagId,
            granteePoid,
        });
    }

    /**
     * Checks if a pending permission request exists for a new user (not yet a Vanguard client).
     * 
     * @param sagId - Service Agreement Group ID
     * @param granteeEmail - The email address of the potential grantee
     * @returns Observable<PendingRequestResponse> - Indicates if a pending request exists
     * 
     * @remarks
     * Used in the Grant Access Flow when adding someone who doesn't have a Vanguard account.
     * Helps prevent sending duplicate invitations to the same email address.
     */
    checkRequestForNewUser(
        sagId: string,
        granteeEmail: string,
    ): Observable<PendingRequestResponse> {
        return this.checkRequest({
            sagId,
            granteeEmail,
        });
    }

    /**
     * Makes the API call to check for pending permission requests.
     * 
     * @param body - Payload containing either granteePoid (existing user) or granteeEmail (new user)
     * @returns Observable<PendingRequestResponse> - Indicates if a pending request exists
     * 
     * @private
     */
    private checkRequest(body: PendingRequestPayload): Observable<PendingRequestResponse> {
        return this.httpClient
            .post<PendingRequestResponse>(
                `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.CHECK_PENDING_REQUEST}`,
                body,
            )
            .pipe(
                catchError((e) => {
                    this.loggerService.error(
                        `${
                            PendingRequestService.name
                        }: error while checking pending request for ${JSON.stringify(body)}`,
                    );
                    return throwError(() => e);
                }),
            );
    }
   checkPendingRequestsMulti(
        dto: CheckPendingRequestsDto,
    ): Observable<CheckPendingRequestsResponseDto> {
        return this.httpClient
            .post<CheckPendingRequestsResponseDto>(
                `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.CHECK_PENDING_REQUESTS_MULTI}`,
                dto,
            )
            .pipe(
                catchError((e) => {
                    this.loggerService.error(
                        `${PendingRequestService.name}: error while checking pending requests for multiple accounts`,
                    );
                    return throwError(() => e);
                }),
            );
    }
}



